import { FC, useRef, useMemo, useEffect } from 'react'
import * as React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { TabBar } from 'antd-mobile'
import { styled } from 'styled-components'

import * as ROUTES from 'src/constants/routes'
import { useAppSelector } from 'src/store/hooks'
import { useIsOrganisation } from 'src/hooks/useIsOrganisation'

import HomeIcon from 'src/assets/icons/navbar/home.svg?react'
import HomeActiveIcon from 'src/assets/icons/navbar/home-active.svg?react'
import ChatIcon from 'src/assets/icons/navbar/chat.svg?react'
import ChatActiveIcon from 'src/assets/icons/navbar/chat-active.svg?react'
import ConnectionsIcon from 'src/assets/icons/navbar/connections.svg?react'
import ConnectionsActiveIcon from 'src/assets/icons/navbar/connections-active.svg?react'
import MapIcon from 'src/assets/icons/navbar/map.svg?react'
import MapActiveIcon from 'src/assets/icons/navbar/map-active.svg?react'
import EventsIcon from 'src/assets/icons/navbar/events.svg?react'
import EventsActiveIcon from 'src/assets/icons/navbar/events-active.svg?react'

const NAVBAR_BG = '#312783'

const StyledNav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  background: ${NAVBAR_BG};
  padding: 12px;

  /* Ensure it stays fixed on Chrome mobile */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;

  /* Hide on screens wider than mobile */
  @media (min-width: 901px) {
    display: none;
  }
`

const StyledTabBar = styled(TabBar)<{ $orgMode?: boolean }>`
  width: 100%;

  .adm-tab-bar-item {
    color: rgba(255, 255, 255, 0.8);
    width: 64px;
    height: 64px;
    justify-content: center;
  }

  .adm-tab-bar-item-active {
    color: #ffffff;
  }

  .adm-tab-bar-item-title {
    font-size: 0.7rem;
  }

  ${({ $orgMode }) =>
    $orgMode &&
    `
      .adm-tab-bar-item:nth-last-child(-n + 2) {
        visibility: hidden;
        pointer-events: none;
      }
    `}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: auto;
    height: 22px;
  }
`

export const BottomTabBar: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const tabBarRef = useRef<HTMLDivElement>(null)

  const isOrganization = useIsOrganisation()

  const unreadCounts = useAppSelector((state) => state.messages.unreadCounts)
  const totalUnreadMessages = Object.values(unreadCounts).reduce((a, b) => a + b, 0)

  const getActiveKey = (): string => {
    const { pathname } = location
    if (pathname.startsWith(ROUTES.PRIVATE_CHAT)) return ROUTES.PRIVATE_CHAT
    if (pathname.startsWith(ROUTES.PRIVATE_CONNECTIONS)) return ROUTES.PRIVATE_CONNECTIONS
    if (pathname.startsWith(ROUTES.PRIVATE_MAP)) return ROUTES.PRIVATE_MAP
    if (pathname.startsWith(ROUTES.PRIVATE_CALENDAR)) return ROUTES.PRIVATE_CALENDAR
    if (pathname === ROUTES.PRIVATE_HOME) return ROUTES.PRIVATE_HOME
    return ''
  }

  const activeKey = getActiveKey()

  const setRouteActive = (value: string) => {
    if (value.startsWith('__spacer')) return
    void navigate(value)
  }

  interface TabItem {
    key: string
    title: string
    icon: React.ReactNode
    badge?: React.ReactNode
  }

  const tabs: TabItem[] = useMemo(() => {
    const allTabs: TabItem[] = [
      {
        key: ROUTES.PRIVATE_HOME,
        title: 'Home',
        icon: (
          <IconWrapper aria-hidden="true">
            {activeKey === ROUTES.PRIVATE_HOME ? <HomeActiveIcon /> : <HomeIcon />}
          </IconWrapper>
        ),
      },
      {
        key: ROUTES.PRIVATE_CHAT,
        title: 'Chat',
        icon: (
          <IconWrapper aria-hidden="true">
            {activeKey === ROUTES.PRIVATE_CHAT ? <ChatActiveIcon /> : <ChatIcon />}
          </IconWrapper>
        ),
        badge: totalUnreadMessages > 0 ? totalUnreadMessages : undefined,
      },
      {
        key: ROUTES.PRIVATE_CONNECTIONS,
        title: 'Connections',
        icon: (
          <IconWrapper aria-hidden="true">
            {activeKey === ROUTES.PRIVATE_CONNECTIONS ? (
              <ConnectionsActiveIcon />
            ) : (
              <ConnectionsIcon />
            )}
          </IconWrapper>
        ),
      },
      {
        key: ROUTES.PRIVATE_MAP,
        title: 'Map',
        icon: (
          <IconWrapper aria-hidden="true">
            {activeKey === ROUTES.PRIVATE_MAP ? <MapActiveIcon /> : <MapIcon />}
          </IconWrapper>
        ),
      },
      {
        key: ROUTES.PRIVATE_CALENDAR,
        title: 'My events',
        icon: (
          <IconWrapper aria-hidden="true">
            {activeKey === ROUTES.PRIVATE_CALENDAR ? <EventsActiveIcon /> : <EventsIcon />}
          </IconWrapper>
        ),
      },
    ]

    if (isOrganization) {
      const restrictedKeys = [ROUTES.PRIVATE_CHAT, ROUTES.PRIVATE_CONNECTIONS]
      const filtered = allTabs.filter((tab) => !restrictedKeys.includes(tab.key))
      // Add invisible spacer items so the 3 real items stay left-aligned instead of
      // being spread across the entire bar width.
      return [
        ...filtered,
        { key: '__spacer-1', title: '', icon: null },
        { key: '__spacer-2', title: '', icon: null },
      ]
    }

    return allTabs
  }, [totalUnreadMessages, isOrganization, activeKey])

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!tabBarRef.current) return

      const tabElements = tabBarRef.current.querySelectorAll('[role="tab"]')
      const currentIndex = Array.from(tabElements).findIndex(
        (el) => el.getAttribute('aria-selected') === 'true'
      )

      let newIndex = currentIndex

      switch (e.key) {
        case 'ArrowLeft':
          newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1
          break
        case 'ArrowRight':
          newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0
          break
        case 'Home':
          newIndex = 0
          break
        case 'End':
          newIndex = tabs.length - 1
          break
        default:
          return
      }

      if (newIndex !== currentIndex) {
        e.preventDefault()
        void navigate(tabs[newIndex].key)
      }
    }

    const currentRef = tabBarRef.current
    if (currentRef) {
      currentRef.addEventListener('keydown', handleKeyDown)
      return () => {
        currentRef.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [navigate, tabs])

  return (
    <>
      {/* Chrome mobile position:fixed workaround */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '1px',
          height: '1px',
          pointerEvents: 'none',
          opacity: 0,
        }}
        aria-hidden="true"
      />
      <StyledNav aria-label="Main navigation" role="navigation">
        <div ref={tabBarRef} role="tablist" aria-label="Main navigation tabs">
          <StyledTabBar
            $orgMode={isOrganization}
            activeKey={activeKey}
            onChange={setRouteActive}
            safeArea={true}
          >
            {tabs.map((item) => (
              <TabBar.Item key={item.key} title={item.title} icon={item.icon} badge={item.badge} />
            ))}
          </StyledTabBar>
        </div>
        {/* Announcement region for screen readers */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          {activeKey && `Current page: ${tabs.find((t) => t.key === activeKey)?.title}`}
        </div>
      </StyledNav>
    </>
  )
}
